import React, {FC, ReactNode, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {Link, useOutletContext, useParams} from "react-router-dom";
import {Alert, Segmented, Spin, Typography} from "antd";
import {
    EventRegistrationFormTemplateStatus,
    useEventFeePricesListQuery,
    useEventFeeTypesListQuery,
    useEventHotelsListQuery,
    useEventOptionsListQuery,
    useEventOverviewSummaryQuery,
    useEventPaymentMethodsQuery,
    useEventPricePeriodsListQuery,
    useEventRegistrationFormTemplatesForSetupQuery,
    useEventRoomAllotmentsListQuery,
    useEventRoomTypesListQuery,
    useEventsListQuery,
    PaymentMethodCategory
} from "../../GraphQL/generated/graphql";
import type {SelectedEventWorkspaceContextValue} from "./EventWorkspaceLayout";
import {eventWorkspaceRoute} from "./eventWorkspaceRoutes";
import {buildUserFacingErrorLabels, getUserFacingErrorMessage} from "../../userFacingErrorUtils";
import {formatLocalDate} from "./adminDateFormat";
import "../../Styles/EventSetupPage.css";
import {StatusTag} from "../../Components/ui/StatusTag";
import type {StatusTone} from "../../Components/ui/StatusTag";
import {KpiCard, KpiGrid, PageHeader} from "../../Components/ui";

const {Text, Title} = Typography;

type TrendRangeOption = "last7Days" | "last30Days" | "last6Months";
type TrendGranularityOption = "day" | "week" | "month";

type EventPaymentMethodCategory = PaymentMethodCategory;

type EventPaymentMethodConfig = {
    category: EventPaymentMethodCategory;
    bankTransferAllowed: boolean;
    cardAllowed: boolean;
    posAllowed: boolean;
};

type SetupSection = {
    key: string;
    title: string;
    action: string;
    route: string;
    statusLabel: string;
    statusTone: StatusTone;
};

type OverviewKpi = {
    key: string;
    value: number;
    label: string;
};

type OverviewBandProps = {
    title: string;
    children: ReactNode;
};

type TrendSeriesPoint = {
    date: string;
    registrationCount: number;
    reservationCount: number;
};

type TrendChartCoordinates = {
    x: number;
    registrationY: number;
    reservationY: number;
    date: string;
};

const textReady = (value?: string | null) => !!value?.trim();
const hasAnyPaymentMethodEnabled = (item?: EventPaymentMethodConfig | null) =>
    !!item && (item.bankTransferAllowed || item.cardAllowed || item.posAllowed);
const localDateKey = (value: unknown) => {
    const date = value instanceof Date ? value : new Date(String(value ?? ""));
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const TREND_VIEWBOX_WIDTH = 860;
const TREND_VIEWBOX_HEIGHT = 250;
const TREND_PADDING_LEFT = 44;
const TREND_PADDING_RIGHT = 24;
const TREND_PADDING_TOP = 40;
const TREND_PADDING_BOTTOM = 34;
const TREND_GRID_LINE_COUNT = 4;
const TREND_TICK_COUNT = 7;

const buildSmoothPath = (points: Array<{ x: number; y: number }>) => {
    if (points.length === 0) {
        return "";
    }

    if (points.length === 1) {
        const point = points[0];
        return `M ${point.x} ${point.y}`;
    }

    let path = `M ${points[0].x} ${points[0].y}`;

    for (let i = 0; i < points.length - 1; i += 1) {
        const previous = points[i - 1] ?? points[i];
        const current = points[i];
        const next = points[i + 1];
        const afterNext = points[i + 2] ?? next;

        const controlPoint1X = current.x + (next.x - previous.x) / 6;
        const controlPoint1Y = current.y + (next.y - previous.y) / 6;
        const controlPoint2X = next.x - (afterNext.x - current.x) / 6;
        const controlPoint2Y = next.y - (afterNext.y - current.y) / 6;

        path += ` C ${controlPoint1X} ${controlPoint1Y}, ${controlPoint2X} ${controlPoint2Y}, ${next.x} ${next.y}`;
    }

    return path;
};

const sanitizeTrendCount = (value?: number | null) => {
    const numericValue = typeof value === "number"
        ? value
        : 0;

    return Number.isFinite(numericValue) && numericValue > 0
        ? Math.trunc(numericValue)
        : 0;
};

const normalizeTrendPoints = (
    points?: Array<{
        date: string;
        registrationCount: number;
        reservationCount: number;
    }> | null
): TrendSeriesPoint[] => {
    const normalized = (points ?? [])
        .filter(point => !!point?.date)
        .map(point => ({
            date: point.date,
            registrationCount: sanitizeTrendCount(point.registrationCount),
            reservationCount: sanitizeTrendCount(point.reservationCount)
        }));

    if (normalized.length > 0) {
        return normalized;
    }

    const today = new Date().toISOString().slice(0, 10);
    return [{
        date: today,
        registrationCount: 0,
        reservationCount: 0
    }];
};

const computeTrendChartCoordinates = (points: TrendSeriesPoint[]): TrendChartCoordinates[] => {
    const plotWidth = TREND_VIEWBOX_WIDTH - TREND_PADDING_LEFT - TREND_PADDING_RIGHT;
    const plotHeight = TREND_VIEWBOX_HEIGHT - TREND_PADDING_TOP - TREND_PADDING_BOTTOM;
    const maxValue = Math.max(
        1,
        ...points.map(point => Math.max(point.registrationCount, point.reservationCount))
    );
    const step = points.length > 1
        ? plotWidth / (points.length - 1)
        : 0;

    return points.map((point, index) => {
        const x = TREND_PADDING_LEFT + step * index;
        const registrationY = TREND_PADDING_TOP + (1 - point.registrationCount / maxValue) * plotHeight;
        const reservationY = TREND_PADDING_TOP + (1 - point.reservationCount / maxValue) * plotHeight;

        return {
            x,
            registrationY,
            reservationY,
            date: point.date
        };
    });
};

const buildTrendTickIndexes = (pointCount: number): number[] => {
    if (pointCount <= 0) {
        return [];
    }

    if (pointCount <= TREND_TICK_COUNT) {
        return Array.from({length: pointCount}, (_, index) => index);
    }

    return Array.from({length: TREND_TICK_COUNT}, (_, tick) =>
        Math.round((tick / (TREND_TICK_COUNT - 1)) * (pointCount - 1)));
};

const OverviewBand: FC<OverviewBandProps> = ({children, title}) => (
    <section className="event-overview__band" aria-label={title}>
        <div className="event-overview__band-header">
            <Title className="event-overview__band-title" level={4}>{title}</Title>
        </div>
        <div className="event-overview__band-body">
            {children}
        </div>
    </section>
);

export const EventSetupPage: FC = () => {
    const {t} = useTranslation();
    const errorLabels = buildUserFacingErrorLabels(t);
    const {eventId} = useParams();
    const workspaceContext = useOutletContext<SelectedEventWorkspaceContextValue | undefined>();
    const numericEventId = eventId ? Number(eventId) : undefined;
    const [trendRange, setTrendRange] = useState<TrendRangeOption>("last7Days");
    const [trendGranularity, setTrendGranularity] = useState<TrendGranularityOption>("day");

    const eventQuery = useEventsListQuery({
        variables: {
            take: 1,
            where: numericEventId ? {id: {eq: numericEventId}} : undefined
        },
        skip: !numericEventId
    });

    const paymentMethodsQuery = useEventPaymentMethodsQuery({
        variables: {eventId: numericEventId ?? 0},
        skip: !numericEventId,
        fetchPolicy: "cache-and-network"
    });
    const pricePeriodsQuery = useEventPricePeriodsListQuery({
        variables: {
            take: 100,
            where: numericEventId ? {eventId: {eq: numericEventId}} : undefined
        },
        skip: !numericEventId
    });
    const feeTypesQuery = useEventFeeTypesListQuery({
        variables: {
            take: 100,
            where: numericEventId ? {eventId: {eq: numericEventId}} : undefined
        },
        skip: !numericEventId
    });
    const feePriceWhere = useMemo(() => {
        if (!numericEventId) {
            return undefined;
        }

        const feeTypeIds = (feeTypesQuery.data?.eventFeeTypes?.items ?? []).map(feeType => feeType.id);
        const pricePeriodIds = (pricePeriodsQuery.data?.eventPricePeriods?.items ?? []).map(period => period.id);

        return {
            and: [
                {feeTypeId: {in: feeTypeIds.length ? feeTypeIds : [-1]}},
                {pricePeriodId: {in: pricePeriodIds.length ? pricePeriodIds : [-1]}}
            ]
        };
    }, [feeTypesQuery.data?.eventFeeTypes?.items, numericEventId, pricePeriodsQuery.data?.eventPricePeriods?.items]);
    const feePricesQuery = useEventFeePricesListQuery({
        variables: {
            take: 100,
            where: feePriceWhere
        },
        skip: !numericEventId
    });
    const formTemplatesQuery = useEventRegistrationFormTemplatesForSetupQuery({
        variables: {
            eventId: numericEventId ?? 0
        },
        skip: !numericEventId,
        fetchPolicy: "cache-and-network"
    });

    const hotelsQuery = useEventHotelsListQuery({
        variables: {
            take: 50,
            where: numericEventId ? {eventId: {eq: numericEventId}} : undefined
        },
        skip: !numericEventId
    });

    const hotelIds = useMemo(
        () => (hotelsQuery.data?.eventHotels?.items ?? [])
            .map(hotel => hotel?.id)
            .filter((id): id is number => typeof id === "number"),
        [hotelsQuery.data?.eventHotels?.items]
    );

    const roomTypesQuery = useEventRoomTypesListQuery({
        variables: {
            take: 100,
            where: numericEventId ? {hotelId: {in: hotelIds.length ? hotelIds : [-1]}} : undefined
        },
        skip: !numericEventId
    });

    const roomTypeIds = useMemo(
        () => (roomTypesQuery.data?.eventRoomTypes?.items ?? [])
            .map(roomType => roomType?.id)
            .filter((id): id is number => typeof id === "number"),
        [roomTypesQuery.data?.eventRoomTypes?.items]
    );

    const roomAllotmentsQuery = useEventRoomAllotmentsListQuery({
        variables: {
            take: 100,
            where: numericEventId
                ? {
                    and: [
                        {hotelId: {in: hotelIds.length ? hotelIds : [-1]}},
                        {roomTypeId: {in: roomTypeIds.length ? roomTypeIds : [-1]}}
                    ]
                }
                : undefined
        },
        skip: !numericEventId
    });

    const optionsQuery = useEventOptionsListQuery({
        variables: {
            take: 1,
            where: numericEventId
                ? {
                    eventId: {eq: numericEventId},
                    isActive: {eq: true}
                }
                : undefined
        },
        skip: !numericEventId
    });

    const overviewSummaryQuery = useEventOverviewSummaryQuery({
        variables: {
            eventId: numericEventId ?? 0,
            trendRange,
            trendGranularity
        },
        skip: !numericEventId,
        fetchPolicy: "cache-and-network"
    });

    const workspaceEvent = workspaceContext && workspaceContext.eventId === numericEventId
        ? workspaceContext.event
        : undefined;
    const event = eventQuery.data?.events?.items?.[0] ?? workspaceEvent;
    const paymentMethods = paymentMethodsQuery.data?.eventPaymentMethods?.items ?? [];

    const hotelCount = hotelsQuery.data?.eventHotels?.totalCount ?? 0;
    const roomTypeCount = roomTypesQuery.data?.eventRoomTypes?.totalCount ?? 0;
    const roomAllotmentCount = roomAllotmentsQuery.data?.eventRoomAllotments?.totalCount ?? 0;
    const activeOptionCount = optionsQuery.data?.eventOptions?.totalCount ?? 0;
    const roomingSetupReady = hotelCount > 0 && roomTypeCount > 0 && roomAllotmentCount > 0;
    const overviewSummary = overviewSummaryQuery.data?.overviewSummary;
    const participantSummary = overviewSummary?.participants;
    const paymentSummary = overviewSummary?.payments;
    const accommodationSummary = overviewSummary?.accommodation;
    const certificateSummary = overviewSummary?.certificates;
    const eventHasParticipationFee = (event as {hasParticipationFee?: boolean | null} | undefined)?.hasParticipationFee ?? true;
    const basicSetupReady = !!event
        && textReady(event.name)
        && !!event.startDate
        && !!event.endDate;
    const participationPaymentMethodsReady = !eventHasParticipationFee
        || paymentMethods.some(item => item?.category === "PARTICIPATION" && hasAnyPaymentMethodEnabled(item));
    const publicFeeTypes = (feeTypesQuery.data?.eventFeeTypes?.items ?? [])
        .filter(feeType => !feeType.isPublicRegistrationHidden);
    const publicFeeTypeIds = new Set(publicFeeTypes.map(feeType => feeType.id));
    const pricePeriods = pricePeriodsQuery.data?.eventPricePeriods?.items ?? [];
    const currentDateKey = localDateKey(new Date());
    const currentPricePeriodIds = new Set(pricePeriods
        .filter(period => {
            const startDateKey = localDateKey(period.startDate);
            const endDateKey = localDateKey(period.endDate);
            return !!currentDateKey
                && !!startDateKey
                && !!endDateKey
                && startDateKey <= currentDateKey
                && currentDateKey <= endDateKey;
        })
        .map(period => period.id));
    const currentFeePriceCount = (feePricesQuery.data?.eventFeePrices?.items ?? [])
        .filter(price => publicFeeTypeIds.has(price.feeTypeId) && currentPricePeriodIds.has(price.pricePeriodId))
        .length;
    const pricingSetupReady = !eventHasParticipationFee
        || (
            publicFeeTypes.length > 0
            && pricePeriods.length > 0
            && currentFeePriceCount > 0
            && participationPaymentMethodsReady
        );
    const hasActiveRegistrationForm = (
        formTemplatesQuery.data?.eventRegistrationFormArbitraryFieldsTemplates?.items ?? []
    ).some(template => template.status === EventRegistrationFormTemplateStatus.Active);
    const registrationLinkReady = basicSetupReady
        && pricingSetupReady
        && hasActiveRegistrationForm;

    const currentEventId = numericEventId ?? 0;
    const setupSections = useMemo<SetupSection[]>(() => [
        {
            key: "settings",
            title: t("event-workspace.settings", {defaultValue: "Event basics"}),
            action: t("setup-flow.open-settings", {defaultValue: "Open settings"}),
            route: eventWorkspaceRoute(currentEventId, "settings"),
            statusLabel: basicSetupReady
                ? t("setup-flow.done", {defaultValue: "Ready"})
                : t("setup-flow.todo", {defaultValue: "Incomplete"}),
            statusTone: basicSetupReady ? "success" : "warning"
        },
        {
            key: "registration-fees",
            title: t("event-workspace.registration-fees", {defaultValue: "Fees and payment"}),
            action: t("setup-flow.open-pricing", {defaultValue: "Open pricing"}),
            route: eventWorkspaceRoute(currentEventId, "registration-fees"),
            statusLabel: !eventHasParticipationFee
                ? t("event-overview.fee-disabled", {defaultValue: "Disabled"})
                : pricingSetupReady
                    ? t("setup-flow.done", {defaultValue: "Ready"})
                    : t("setup-flow.todo", {defaultValue: "Incomplete"}),
            statusTone: !eventHasParticipationFee
                ? "neutral"
                : pricingSetupReady
                    ? "success"
                    : "warning"
        },
        {
            key: "accommodation",
            title: t("event-workspace.accommodation", {defaultValue: "Accommodation"}),
            action: t("setup-flow.open-accommodation", {defaultValue: "Open accommodation"}),
            route: eventWorkspaceRoute(currentEventId, "accommodation"),
            statusLabel: roomingSetupReady
                ? t("setup-flow.done", {defaultValue: "Ready"})
                : hotelCount === 0
                    ? t("setup-flow.optional", {defaultValue: "Optional"})
                    : t("setup-flow.todo", {defaultValue: "Incomplete"}),
            statusTone: roomingSetupReady ? "success" : hotelCount === 0 ? "info" : "warning"
        },
        {
            key: "activities",
            title: t("event-workspace.activities", {defaultValue: "Activities"}),
            action: t("setup-flow.open-activities", {defaultValue: "Open activities"}),
            route: eventWorkspaceRoute(currentEventId, "activities"),
            statusLabel: activeOptionCount > 0
                ? t("setup-flow.done", {defaultValue: "Ready"})
                : t("setup-flow.optional", {defaultValue: "Optional"}),
            statusTone: activeOptionCount > 0 ? "success" : "info"
        },
        {
            key: "registration-form",
            title: t("event-workspace.registration-form", {defaultValue: "Form"}),
            action: t("setup-flow.open-registration-form", {defaultValue: "Open registration"}),
            route: eventWorkspaceRoute(currentEventId, "registration-form"),
            statusLabel: registrationLinkReady
                ? t("setup-flow.registration-ready", {defaultValue: "Ready"})
                : !hasActiveRegistrationForm
                    ? t("public-event-forms.readiness-inactive", {defaultValue: "Inactive"})
                    : t("setup-flow.registration-missing", {defaultValue: "Review"}),
            statusTone: registrationLinkReady ? "success" : "warning"
        }
    ], [
        activeOptionCount,
        basicSetupReady,
        currentEventId,
        eventHasParticipationFee,
        hasActiveRegistrationForm,
        hotelCount,
        pricingSetupReady,
        registrationLinkReady,
        roomingSetupReady,
        t
    ]);

    const overviewKpis = useMemo<OverviewKpi[]>(() => [
        {
            key: "total-applications",
            value: participantSummary?.registrationCount ?? 0,
            label: t("event-overview.total-applications", {defaultValue: "Total registrations"})
        },
        {
            key: "accommodation-reservations",
            value: accommodationSummary?.reservationCount ?? 0,
            label: t("event-overview.accommodation-reservations", {defaultValue: "Accommodation reservations"})
        },
        {
            key: "paid-fees",
            value: paymentSummary?.paidCount ?? 0,
            label: t("event-overview.paid-fees", {defaultValue: "Paid fees"})
        },
        {
            key: "certificates-ready-or-sent",
            value: certificateSummary?.readyOrSentCount ?? 0,
            label: t("event-overview.certificates-ready-sent", {defaultValue: "Certificates ready/sent"})
        }
    ], [
        accommodationSummary?.reservationCount,
        certificateSummary?.readyOrSentCount,
        participantSummary?.registrationCount,
        paymentSummary?.paidCount,
        t
    ]);
    const trendGranularityOptions = useMemo(() => [
        {label: t("event-overview.trend-days", {defaultValue: "Days"}), value: "day"},
        {label: t("event-overview.trend-weeks", {defaultValue: "Weeks"}), value: "week"},
        {label: t("event-overview.trend-months", {defaultValue: "Months"}), value: "month"}
    ], [t]);
    const trendRangeOptions = useMemo(() => [
        {label: t("event-overview.trend-last-7-days", {defaultValue: "Last 7 days"}), value: "last7Days"},
        {label: t("event-overview.trend-last-30-days", {defaultValue: "Last 30 days"}), value: "last30Days"},
        {label: t("event-overview.trend-last-6-months", {defaultValue: "Last 6 months"}), value: "last6Months"}
    ], [t]);

    const trendSeriesPoints = useMemo(
        () => normalizeTrendPoints(overviewSummary?.trend?.points),
        [overviewSummary?.trend?.points]
    );
    const trendCoordinates = useMemo(
        () => computeTrendChartCoordinates(trendSeriesPoints),
        [trendSeriesPoints]
    );
    const trendRegistrationPath = useMemo(
        () => buildSmoothPath(trendCoordinates.map(point => ({x: point.x, y: point.registrationY}))),
        [trendCoordinates]
    );
    const trendReservationPath = useMemo(
        () => buildSmoothPath(trendCoordinates.map(point => ({x: point.x, y: point.reservationY}))),
        [trendCoordinates]
    );
    const trendTickIndexes = useMemo(
        () => buildTrendTickIndexes(trendCoordinates.length),
        [trendCoordinates.length]
    );
    const trendMaxValue = useMemo(
        () => Math.max(
            1,
            ...trendSeriesPoints.map(point => Math.max(point.registrationCount, point.reservationCount))
        ),
        [trendSeriesPoints]
    );
    const trendRangeLabel = useMemo(() => {
        const startDate = overviewSummary?.trend?.rangeStartDate ?? trendSeriesPoints[0]?.date;
        const endDate = overviewSummary?.trend?.rangeEndDate ?? trendSeriesPoints[trendSeriesPoints.length - 1]?.date;

        if (!startDate || !endDate) {
            return "-";
        }

        const startLabel = formatLocalDate(startDate, "-");
        const endLabel = formatLocalDate(endDate, "-");

        return `${startLabel} - ${endLabel}`;
    }, [
        overviewSummary?.trend?.rangeEndDate,
        overviewSummary?.trend?.rangeStartDate,
        trendSeriesPoints
    ]);

    const loading = eventQuery.loading
        || paymentMethodsQuery.loading
        || pricePeriodsQuery.loading
        || feeTypesQuery.loading
        || feePricesQuery.loading
        || formTemplatesQuery.loading
        || hotelsQuery.loading
        || roomTypesQuery.loading
        || roomAllotmentsQuery.loading
        || optionsQuery.loading
        || overviewSummaryQuery.loading;

    if (!numericEventId) {
        return (
            <div className="ati-workspace-page">
                <PageHeader title={t("event")} level={2}/>
                <Alert
                    type="warning"
                    message={t("event")}
                    description={t("no-event-selected", {defaultValue: "No event selected."})}
                />
            </div>
        );
    }

    return (
        <div className="ati-workspace-page">
            <PageHeader title={t("event-workspace.overview", {defaultValue: "Overview"})} level={2}/>
            {loading && !event && <Spin />}
            {!loading && !event && (
                <Alert
                    type="error"
                    message={t("event")}
                    description={t("event-workspace.not-found", {defaultValue: "Event not found."})}
                />
            )}

            {event && (
                <div className="event-overview">
                    <KpiGrid aria-label={t("event-overview.quick-totals", {defaultValue: "Event summary"})}>
                        {overviewKpis.map(kpi => <KpiCard key={kpi.key} value={kpi.value} label={kpi.label}/>)}
                    </KpiGrid>

                    <section
                        className="event-overview__trend"
                        aria-label={t("event-overview.trend-title", {defaultValue: "Event status"})}
                    >
                        <div className="event-overview__trend-header">
                            <div className="event-overview__trend-copy">
                                <Title className="event-overview__trend-title" level={4}>
                                    {t("event-overview.trend-title", {defaultValue: "Event status"})}
                                </Title>
                                <Text className="event-overview__trend-range" type="secondary">
                                    {trendRangeLabel}
                                </Text>
                            </div>
                            <div className="event-overview__trend-tools">
                                <div className="event-overview__trend-controls">
                                    <div className="event-overview__trend-control">
                                        <Text className="event-overview__trend-control-label">
                                            {t("event-overview.trend-group-by", {defaultValue: "Group by"})}
                                        </Text>
                                        <Segmented
                                            aria-label={t("event-overview.trend-group-by", {defaultValue: "Group by"})}
                                            onChange={(value) => setTrendGranularity(value as TrendGranularityOption)}
                                            options={trendGranularityOptions}
                                            size="small"
                                            value={trendGranularity}
                                        />
                                    </div>
                                    <div className="event-overview__trend-control">
                                        <Text className="event-overview__trend-control-label">
                                            {t("event-overview.trend-period", {defaultValue: "Period"})}
                                        </Text>
                                        <Segmented
                                            aria-label={t("event-overview.trend-period", {defaultValue: "Period"})}
                                            onChange={(value) => setTrendRange(value as TrendRangeOption)}
                                            options={trendRangeOptions}
                                            size="small"
                                            value={trendRange}
                                        />
                                    </div>
                                </div>
                                <div className="event-overview__trend-legend">
                                    <span className="event-overview__trend-legend-item">
                                        <span className="event-overview__trend-legend-dot event-overview__trend-legend-dot--registrations"/>
                                        {t("event-overview.trend-registrations", {defaultValue: "Registrations"})}
                                    </span>
                                    <span className="event-overview__trend-legend-item">
                                        <span className="event-overview__trend-legend-dot event-overview__trend-legend-dot--reservations"/>
                                        {t("event-overview.trend-reservations", {defaultValue: "Reservations"})}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <svg
                            className="event-overview__trend-chart"
                            viewBox={`0 0 ${TREND_VIEWBOX_WIDTH} ${TREND_VIEWBOX_HEIGHT}`}
                            role="img"
                            aria-label={t("event-overview.trend-chart-aria", {defaultValue: "Registration and reservation trend"})}
                        >
                            {Array.from({length: TREND_GRID_LINE_COUNT + 1}, (_, index) => {
                                const ratio = index / TREND_GRID_LINE_COUNT;
                                const y = TREND_PADDING_TOP + ratio * (TREND_VIEWBOX_HEIGHT - TREND_PADDING_TOP - TREND_PADDING_BOTTOM);
                                const yValue = Math.round((1 - ratio) * trendMaxValue);

                                return (
                                    <g key={`grid-${index}`}>
                                        <line
                                            className="event-overview__trend-grid-line"
                                            x1={TREND_PADDING_LEFT}
                                            y1={y}
                                            x2={TREND_VIEWBOX_WIDTH - TREND_PADDING_RIGHT}
                                            y2={y}
                                        />
                                        <text
                                            className="event-overview__trend-grid-label"
                                            x={TREND_PADDING_LEFT - 10}
                                            y={y + 4}
                                            textAnchor="end"
                                        >
                                            {yValue}
                                        </text>
                                    </g>
                                );
                            })}
                            <path className="event-overview__trend-line event-overview__trend-line--registrations" d={trendRegistrationPath}/>
                            <path className="event-overview__trend-line event-overview__trend-line--reservations" d={trendReservationPath}/>
                            {trendCoordinates.map((point, index) => (
                                <g key={`point-${point.date}-${index}`}>
                                    <circle
                                        className="event-overview__trend-dot event-overview__trend-dot--registrations"
                                        cx={point.x}
                                        cy={point.registrationY}
                                        r={4}
                                    />
                                    <circle
                                        className="event-overview__trend-dot event-overview__trend-dot--reservations"
                                        cx={point.x}
                                        cy={point.reservationY}
                                        r={4}
                                    />
                                </g>
                            ))}
                            {trendTickIndexes.map(index => {
                                const point = trendCoordinates[index];
                                if (!point) {
                                    return null;
                                }

                                return (
                                    <text
                                        key={`tick-${point.date}-${index}`}
                                        className="event-overview__trend-tick-label"
                                        x={point.x}
                                        y={TREND_VIEWBOX_HEIGHT - 8}
                                        textAnchor="middle"
                                    >
                                        {formatLocalDate(point.date, "-")}
                                    </text>
                                );
                            })}
                        </svg>
                    </section>

                    {overviewSummaryQuery.error && (
                        <Alert
                            type="error"
                            message={t("event-overview.signals-unavailable", {defaultValue: "Event signals unavailable"})}
                            description={getUserFacingErrorMessage(overviewSummaryQuery.error, t, errorLabels)}
                        />
                    )}

                    <div className="event-overview__bands">
                        <OverviewBand
                            title={t("event-overview.guided-setup", {defaultValue: "Guided event setup"})}
                        >
                            <div className="event-overview__list">
                                {setupSections.map(section => (
                                    <div className="event-overview__row" key={section.key}>
                                        <Text strong>{section.title}</Text>
                                        <StatusTag tone={section.statusTone}>{section.statusLabel}</StatusTag>
                                        <Link className="event-overview__action" to={section.route}>
                                            {section.action}
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        </OverviewBand>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventSetupPage;
